import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Animated } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';

interface SkeletonLoaderProps {
  width?: number | string;
  height?: number;
  borderRadius?: number;
  style?: any;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  width = '100%',
  height = 20,
  borderRadius = 4,
  style,
}) => {
  const { theme } = useTheme();
  const animatedValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(animatedValue, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: false,
        }),
        Animated.timing(animatedValue, {
          toValue: 0,
          duration: 1000,
          useNativeDriver: false,
        }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, []);

  const backgroundColor = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [theme.colors.border, theme.colors.card],
  });

  return (
    <Animated.View
      style={[
        {
          width,
          height,
          borderRadius,
          backgroundColor,
        },
        style,
      ]}
    />
  );
};

interface DashboardSkeletonProps {}

export const DashboardSkeleton: React.FC<DashboardSkeletonProps> = () => {
  const { theme } = useTheme();

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header Skeleton */}
      <View style={[styles.header, { backgroundColor: theme.colors.primary }]}>
        <View style={styles.headerContent}>
          <View style={{ flex: 1 }}>
            <SkeletonLoader width={80} height={16} style={{ marginBottom: 8 }} />
            <SkeletonLoader width={150} height={24} style={{ marginBottom: 4 }} />
            <SkeletonLoader width={120} height={12} />
          </View>
          <SkeletonLoader width={44} height={44} borderRadius={22} />
        </View>
      </View>

      {/* Stats Grid Skeleton */}
      <View style={styles.content}>
        <View style={styles.statsGrid}>
          {[1, 2, 3, 4].map((i) => (
            <View key={i} style={[styles.statCard, { backgroundColor: theme.colors.card }]}>
              <SkeletonLoader
                width={48}
                height={48}
                borderRadius={24}
                style={{ marginBottom: 8 }}
              />
              <SkeletonLoader width={40} height={28} style={{ marginBottom: 4 }} />
              <SkeletonLoader width={60} height={14} />
            </View>
          ))}
        </View>

        {/* Warnings Section Skeleton */}
        <View style={[styles.section, { backgroundColor: theme.colors.card }]}>
          <View style={styles.sectionHeader}>
            <SkeletonLoader width={20} height={20} borderRadius={10} />
            <SkeletonLoader width={120} height={18} style={{ marginLeft: 8 }} />
          </View>
          <SkeletonLoader width="100%" height={50} borderRadius={8} style={{ marginTop: 12 }} />
        </View>

        {/* Alarms Section Skeleton */}
        <View style={[styles.section, { backgroundColor: theme.colors.card }]}>
          <View style={styles.sectionHeader}>
            <SkeletonLoader width={20} height={20} borderRadius={10} />
            <SkeletonLoader width={140} height={18} style={{ marginLeft: 8, flex: 1 }} />
            <SkeletonLoader width={60} height={14} />
          </View>
          {[1, 2, 3].map((i) => (
            <View key={i} style={styles.alarmItem}>
              <SkeletonLoader width={10} height={10} borderRadius={5} />
              <View style={{ flex: 1, marginLeft: 12 }}>
                <SkeletonLoader width="70%" height={15} style={{ marginBottom: 4 }} />
                <SkeletonLoader width="50%" height={13} />
              </View>
              <SkeletonLoader width={50} height={12} />
            </View>
          ))}
        </View>

        {/* Quick Actions Skeleton */}
        <View style={styles.quickActions}>
          {[1, 2, 3, 4].map((i) => (
            <View key={i} style={[styles.actionCard, { backgroundColor: theme.colors.card }]}>
              <SkeletonLoader
                width={32}
                height={32}
                borderRadius={16}
                style={{ marginBottom: 8 }}
              />
              <SkeletonLoader width={80} height={16} style={{ marginBottom: 4 }} />
              <SkeletonLoader width={100} height={12} />
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

const cardWidth = 160;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 20,
    paddingTop: 50,
    paddingBottom: 30,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  content: {
    padding: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  statCard: {
    width: cardWidth,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  section: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  alarmItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  quickActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  actionCard: {
    width: cardWidth,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    minHeight: 120,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
});
